import { useState, useEffect } from "react"
import { getConfig, updateConfig } from "../api/client"

// What a failed save actually says. Saving used to report only the
// client-side JSON.parse message, so a server rejection — an `llm_routing`
// rule naming a call_type that doesn't exist, say — rendered as an empty
// string next to a "Config saved successfully" that never appeared. The
// backend's HTTPException handler wraps the detail as {"error": ...} rather
// than FastAPI's default {"detail": ...}; both are read here so this keeps
// working if that handler is ever dropped.
const saveErrorMessage = (err: unknown): string => {
    const data = (err as { response?: { data?: unknown } })?.response?.data
    if (typeof data === "string" && data.trim()) return data
    if (data && typeof data === "object") {
        const { error, detail } = data as { error?: unknown; detail?: unknown }
        const reported = error ?? detail
        if (typeof reported === "string" && reported.trim()) return reported
        if (reported !== undefined) return JSON.stringify(reported)
    }
    if (err instanceof Error) return err.message
    return String(err)
}

const ConfigPage = () => {
    const [config, setConfig] = useState("")
    const [error, setError] = useState("")
    const [saveStatus, setSaveStatus] = useState("")

    useEffect(() => {
        loadConfig()
    }, [])

    const loadConfig = async () => {
        try {
            const data = await getConfig()
            setConfig(JSON.stringify(data, null, 4))
            setError("")
        } catch (err) {
            setError("Failed to load config: " + err.message)
        }
    }

    const handleSave = async () => {
        try {
            // Validate JSON
            JSON.parse(config)
            
            await updateConfig(config)
            setSaveStatus("Config saved successfully")
            setTimeout(() => setSaveStatus(""), 3000)
            setError("")
        } catch (err) {
            setSaveStatus("")
            setError(saveErrorMessage(err))
        }
    }

    const handleChange = (e) => {
        setConfig(e.target.value)
        setSaveStatus("")
        try {
            JSON.parse(e.target.value)
            setError("")
        } catch (err) {
            setError("Invalid JSON: " + err.message)
        }
    }

    const formatJson = () => {
        try {
            const formatted = JSON.stringify(JSON.parse(config), null, 4)
            setConfig(formatted)
            setError("")
        } catch (err) {
            setError("Invalid JSON: " + err.message)
        }
    }

    return (
        <div className="container mx-auto px-4 mt-4">
            <div className="flex flex-col">
                <div className="w-full">
                    <h2 className="text-2xl font-bold mb-4">Server Configuration</h2>
                    
                    <div className="mb-3">
                        <div className="flex justify-end gap-2 mb-2">
                            <button 
                                className="bg-secondary hover:bg-secondary-dark text-gray-700 font-medium py-2 px-4 rounded-md"
                                onClick={formatJson}
                            >
                                Format JSON
                            </button>
                            <button 
                                className="bg-action hover:bg-action-dark text-plum font-medium py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleSave}
                                disabled={!!error}
                            >
                                Save Changes
                            </button>
                        </div>
                        
                        <textarea
                            className={`w-full p-2 border rounded-md font-mono ${error ? "border-rose-500" : "border-gray-300"}`}
                            value={config}
                            onChange={handleChange}
                            rows="20"
                        />
                        
                        {error && (
                            <div className="text-rose-500 mt-1">
                                {error}
                            </div>
                        )}
                        
                        {saveStatus && (
                            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mt-2 rounded">
                                {saveStatus}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ConfigPage